import { usePagination } from "@/hooks/usePagination"
import { getSearchWith } from "@/utils/searchHelper"
import { FC, useCallback, useEffect, useRef, useState } from "react"
import { useSearchParams } from "react-router-dom"
import { useAuth } from "../../../../../components/auth"
import { useGetEventListLazyQuery, useGetRegionsQuery } from "../../../../../graphql"
import { formatDate } from "../../../../../utils"
import Filter from "./components/Filter"
import List from "./components/List"
import "./index.less"

const DEFAULT_FILTERS: { [key: string]: string } = {
  selectedStatus: "Ongoing",
  selectedRegion: "All",
  selectedTitle: "All",
  selectedAddress: "All",
}

const ListBlock: FC = () => {
  const { region } = useAuth()
  const [getEventsList] = useGetEventListLazyQuery()

  const [loading, setLoading] = useState<boolean>(false)
  const [exportLoading, setExportLoading] = useState<boolean>(false)

  const { page, pageSize, setPage, setPageSize } = usePagination()

  const [searchParams, setSearchParams] = useSearchParams()

  const filters = Object.keys(DEFAULT_FILTERS).reduce(
    (acc, key) => {
      const defaultValue = DEFAULT_FILTERS[key]
      const value = searchParams.get(key)
      acc[key] = value === null ? defaultValue : value
      return acc
    },
    {} as { [key: string]: string }
  )

  const filtersKey = searchParams.toString()

  const setFilters = useCallback(
    (newFilters: { [key: string]: string }) => {
      const params: Record<string, string | null> = {
        page: "1",
        pageSize: "10",
      }

      Object.keys(DEFAULT_FILTERS).forEach(key => {
        const value = key in newFilters ? newFilters[key] : DEFAULT_FILTERS[key]
        const defaultValue = DEFAULT_FILTERS[key]

        params[key] = value === defaultValue ? null : value
      })

      setSearchParams(getSearchWith(searchParams, params))
    },
    [searchParams, setSearchParams]
  )

  // eslint-disable-next-line
  const [list, setList] = useState<any>()
  // eslint-disable-next-line
  const [listForExport, setListForExport] = useState<any>()
  const [totalItems, setTotalItems] = useState<number>(0)

  const downloadBtnRef = useRef<HTMLButtonElement & { link: HTMLAnchorElement | null }>(null)

  const { data: regionsData } = useGetRegionsQuery()
  const getRegionOptions = (): SelectItems => {
    const regionOptions =
      Array.from({ length: regionsData?.regions?.data?.length || 0 }, (_, i) => {
        const item = regionsData?.regions?.data?.[i]
        return {
          label: item?.attributes?.name ?? "",
          value: item?.attributes?.name ?? "",
          key: item?.id ?? String(i),
        }
      }) || []
    regionOptions.unshift({
      label: "All",
      value: "All",
      key: "0",
    })
    return regionOptions
  }

  const regionOptions = getRegionOptions()

  const createQueryFilters = () => {
    const foundRegionKey = regionOptions.find(option => option.label === filters.selectedRegion)?.key

    return {
      type: filters.selectedStatus.toLowerCase(),
      ...(filters?.selectedTitle !== "All" ? { title: filters?.selectedTitle } : {}),
      ...(filters?.selectedAddress !== "All" ? { address: filters?.selectedAddress } : {}),
      ...(filters.selectedRegion !== "All" || region?.id
        ? { region: Number(region?.id) || Number(foundRegionKey) || 0 }
        : {}),
    }
  }

  const createList = async (isForExport = false) => {
    setLoading(true)
    if (list && !isForExport) {
      setList(undefined)
    }

    const newList = await getEventsList({
      fetchPolicy: "network-only",
      variables: {
        input: {
          page: isForExport ? 1 : page,
          pageSize: isForExport ? 1000 : pageSize,
          ...createQueryFilters(),
        },
      },
    })

    const totalItems = newList?.data?.events?.total || 0
    const finalData = newList?.data?.events?.data

    if (finalData && !isForExport) {
      setTotalItems(totalItems ?? 0)
      setList(finalData)
      setLoading(false)
      return
    } else {
      setLoading(false)
      return finalData
    }
  }

  const createExportList = async () => {
    setExportLoading(true)
    const list = await createList(true)

    let listForExport =
      // eslint-disable-next-line
      list?.map((item: any) => [
        item?.title || "-",
        formatDate(item?.startAt) || "-",
        formatDate(item?.endAt) || "-",
        item?.address || "-",
      ]) || []
    listForExport = listForExport?.map(item => item.filter(entity => entity !== null))
    listForExport.unshift(["TITLE", "START", "END", "LOCATION"])

    setExportLoading(false)
    setListForExport(listForExport)
  }

  const createSpecificFiltersList = () => {
    const list = []
    if (filters.selectedTitle !== "All")
      list.push({
        option: filters.selectedTitle,
        type: "title",
        backgroundColor: "rgba(215, 163, 171, 0.15)",
        color: "#D7A3AB",
      })
    if (filters.selectedAddress !== "All")
      list.push({
        option: filters.selectedAddress,
        type: "address",
        backgroundColor: "rgba(68, 195, 187, 0.15)",
        color: "#44C3BB",
      })
    return list
  }

  const specificFilters = createSpecificFiltersList()

  useEffect(() => {
    setListForExport(undefined)
    createList() // eslint-disable-next-line
  }, [pageSize, page, filtersKey])

  useEffect(() => {
    // eslint-disable-next-line
    listForExport && downloadBtnRef.current!.link!.click()
  }, [listForExport])

  return (
    <div className={"block-wrapper"}>
      <Filter
        {...{
          filters,
          setFilters,
          regionOptions,
          specificFilters,
        }}
      />
      <List
        {...{
          list,
          page,
          loading,
          setPage,
          filters,
          pageSize,
          setFilters,
          totalItems,
          setLoading,
          setPageSize,
          exportLoading,
          listForExport,
          downloadBtnRef,
          createExportList,
          createQueryFilters,
        }}
      />
    </div>
  )
}

export default ListBlock
